import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from '@/layouts/AppLayout';
import AuthLayout from '@/layouts/AuthLayout';
import ProtectedRoute from './ProtectedRoute';
// Static, not lazy: ProtectedRoute already imports it for the pricing popup.
import Subscribe from '@/pages/billing/Subscribe';
import PublicRoute from './PublicRoute';
import RequireBusiness from '@/components/RequireBusiness';
import ScheduleSettingsRedirect from './ScheduleSettingsRedirect';
import BusinessSettingsRedirect from './BusinessSettingsRedirect';
import PageFallback from '@/components/PageFallback';
import { lazyRoute } from '@/utils/routeLoading';

// Auth
const Login = lazyRoute(() => import('@/pages/auth/Login'));
const Register = lazyRoute(() => import('@/pages/auth/Register'));
const ForgotPassword = lazyRoute(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazyRoute(() => import('@/pages/auth/ResetPassword'));
const EmailVerification = lazyRoute(() => import('@/pages/auth/EmailVerification'));
const SocialCallback = lazyRoute(() => import('@/pages/auth/SocialCallback'));
const OnboardingBusinessSearch = lazyRoute(() =>
  import('@/pages/auth/OnboardingBusinessSearch')
);

// App
const Dashboard = lazyRoute(() => import('@/pages/dashboard/Dashboard'));
const BusinessesPage = lazyRoute(() => import('@/pages/business/BusinessesPage'));
const BusinessSettings = lazyRoute(() => import('@/pages/business/BusinessSettings'));
const PublicationsStub = lazyRoute(() => import('@/pages/publications/PublicationsStub'));
const CampaignList = lazyRoute(() => import('@/pages/campaigns/CampaignList'));
const CampaignForm = lazyRoute(() => import('@/pages/campaigns/CampaignForm'));
const PackagesPage = lazyRoute(() => import('@/pages/packages/PackagesPage'));
const PackageDetail = lazyRoute(() => import('@/pages/packages/PackageDetail'));
const AutomationsPage = lazyRoute(() => import('@/pages/automations/AutomationsPage'));
const AutomationDashboard = lazyRoute(() => import('@/pages/automations/AutomationDashboard'));
const AdminWeManage = lazyRoute(() => import('@/pages/admin/AdminWeManage'));
const PostList = lazyRoute(() => import('@/pages/posts/PostList'));
const PostEditor = lazyRoute(() => import('@/pages/posts/PostEditor'));
const PostEngagement = lazyRoute(() => import('@/pages/engagement/PostEngagement'));
const EngagementInbox = lazyRoute(() => import('@/pages/engagement/EngagementInbox'));
const CalendarPage = lazyRoute(() => import('@/pages/calendar/CalendarPage'));
const SocialConnections = lazyRoute(() =>
  import('@/pages/socialConnections/SocialConnections')
);
const QrCodesPage = lazyRoute(() => import('@/pages/qr/QrCodesPage'));
const AgencyPage = lazyRoute(() => import('@/pages/agency/AgencyPage'));
const QrScanActivityPage = lazyRoute(() => import('@/pages/qr/QrScanActivityPage'));
// The client-connect link. Outside every layout and every guard — see below.
const PublicConnect = lazyRoute(() => import('@/pages/publicConnect/PublicConnect'));
const VideoStudio = lazyRoute(() => import('@/pages/video/VideoStudio'));
const MediaLibrary = lazyRoute(() => import('@/pages/media/MediaLibrary'));
const LandingPagesList = lazyRoute(() => import('@/pages/landing/LandingPagesList'));
const LandingSiteDashboard = lazyRoute(
  () => import('@/pages/landing/dashboard/LandingSiteDashboard')
);
const LandingBuilder = lazyRoute(() => import('@/pages/landing/builder/LandingBuilder'));
const Billing = lazyRoute(() => import('@/pages/billing/Billing'));
const Settings = lazyRoute(() => import('@/pages/settings/Settings'));
const Profile = lazyRoute(() => import('@/pages/profile/Profile'));
const NotFound = lazyRoute(() => import('@/pages/NotFound'));

export default function AppRoutes() {
  return (
    /*
     * The outermost loading boundary — NOT the one most pages use.
     *
     * Pages inside AppLayout and AuthLayout load inside those layouts' own
     * boundaries, so the navigation stays on screen while a page downloads.
     * This one only catches routes with no layout (the connect link, the admin
     * console, the full-screen builder, 404). It used to be the only boundary,
     * which is why the whole app — nav rail, sidebar and top bar — vanished
     * behind "Loading page..." the first time each page was opened.
     */
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/*
          The client-connect link.

          Not inside PublicRoute, and that is the point: PublicRoute bounces an
          authenticated visitor to /dashboard, so an agency owner clicking their
          own link to check it would never see the page they are about to send a
          client. Not inside AuthLayout either — this is not a sign-in, it has
          no form and no account behind it. It renders its own centred column
          with nothing to navigate away into.
        */}
        <Route path="/connect/:token" element={<PublicConnect />} />

        {/* Public / auth */}
        <Route
          element={
            <PublicRoute>
              <AuthLayout />
            </PublicRoute>
          }
        >
          <Route path="/login" element={<Login />} />
          {/* Public self-service signup (no invite needed since 2026-11). */}
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          {/* Where Google and Facebook return the browser after consent. */}
          <Route path="/auth/callback" element={<SocialCallback />} />
        </Route>

        {/*
          Onboarding is signed-in, but has no sidebar yet — the user has no
          business for the sidebar to be about. So it keeps AuthLayout's centred
          card while sitting behind ProtectedRoute.

          It used to live in the PublicRoute group above, which quietly made it
          unreachable: PublicRoute bounces any authenticated visitor to
          /dashboard, so `login(); navigate('/onboarding')` always landed on the
          dashboard instead. Both the social signup and the email signup's
          token branch depend on this route actually rendering.
        */}
        <Route
          element={
            <ProtectedRoute>
              <AuthLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/onboarding" element={<OnboardingBusinessSearch />} />
        </Route>

        {/* Kept for old links and the 402 redirect in api/client.js. The
            pricing popup itself is rendered by ProtectedRoute for any account
            that still needs to pick a plan, whatever URL it asked for. The
            popup draws its own branded backdrop, so no layout here. */}
        <Route
          path="/subscribe"
          element={
            <ProtectedRoute>
              <Subscribe />
            </ProtectedRoute>
          }
        />

        {/* Protected app */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />

          <Route path="/businesses" element={<BusinessesPage />} />
          <Route path="/businesses/:id/settings" element={<BusinessSettings />} />
          {/* Old URLs, kept for bookmarks. Their pages (a separate create form,
              detail page, edit form and "create project" form) duplicated the
              create dialog and Business settings, and nothing linked to them. */}
          <Route path="/businesses/new" element={<Navigate to="/businesses" replace />} />
          <Route path="/create-project" element={<Navigate to="/businesses" replace />} />
          <Route path="/businesses/:id/edit" element={<BusinessSettingsRedirect />} />
          <Route path="/businesses/:id" element={<BusinessSettingsRedirect />} />

          {/* Publications nav stubs */}
          <Route path="/templates" element={<PublicationsStub kind="templates" />} />
          <Route path="/steps" element={<PublicationsStub kind="steps" />} />
          <Route path="/bin" element={<PublicationsStub kind="bin" />} />

          {/* These pages operate on ONE business — "All Projects" is not valid,
              anything created there would be saved unattached and disappear. */}
          <Route path="/packages" element={<RequireBusiness><PackagesPage /></RequireBusiness>} />
          <Route path="/packages/:id" element={<PackageDetail />} />
          <Route path="/automations" element={<RequireBusiness><AutomationsPage /></RequireBusiness>} />
          <Route path="/automations/:id" element={<AutomationDashboard />} />

          <Route path="/campaigns" element={<CampaignList />} />
          <Route path="/campaigns/new" element={<CampaignForm />} />
          <Route path="/campaigns/:id/edit" element={<CampaignForm />} />

          <Route path="/posts" element={<PostList />} />
          <Route path="/posts/new" element={<PostEditor />} />
          {/* Declared before /posts/:id so "engagement" isn't read as an id. */}
          <Route path="/posts/:id/engagement" element={<PostEngagement />} />
          <Route path="/posts/:id" element={<PostEditor />} />

          {/* Engagement — the inbox is business-scoped like packages/automations. */}
          <Route path="/engagement" element={<Navigate to="/engagement/inbox" replace />} />
          <Route path="/engagement/inbox" element={<RequireBusiness title="Choose a business to view engagement for"><EngagementInbox /></RequireBusiness>} />

          <Route path="/calendar" element={<CalendarPage />} />
          {/* The /:token variant resolves its own business from the link. */}
          <Route path="/social-connections" element={<RequireBusiness title="Choose a business to connect accounts for"><SocialConnections /></RequireBusiness>} />
          <Route path="/social-connections/:token" element={<SocialConnections />} />
          {/* QR codes are per-business, same reasoning as packages/automations/
              social-connections above — "All Projects" has nowhere for a new
              code to be saved. */}
          <Route path="/qr-codes" element={<RequireBusiness title="Choose a business to create QR codes for"><QrCodesPage /></RequireBusiness>} />
          {/* The per-code scan activity dashboard — a specific resource,
              same reasoning as /packages/:id right above: it resolves and
              authorizes its own QR code server-side, so it needs no business
              picker of its own. */}
          <Route path="/qr-codes/:id/activity" element={<QrScanActivityPage />} />
          {/* Publifying Agent — business-scoped like packages/automations/
              qr-codes right above: discovery runs and drafts are all
              per-business, "All Projects" has no agent policy to read. */}
          <Route path="/agency" element={<RequireBusiness title="Choose a business to use the Agency for"><AgencyPage /></RequireBusiness>} />
          {/* Posting schedule is per-business now — keep the old URL working. */}
          <Route path="/schedule-settings" element={<ScheduleSettingsRedirect />} />
          <Route path="/media" element={<MediaLibrary />} />
          {/* Announcement only. Not wrapped in RequireBusiness: there is
              nothing here to scope, and gating it would meet a curious
              user with a business picker instead of the thing they
              clicked. */}
          <Route path="/video" element={<VideoStudio />} />
          {/* Not wrapped in RequireBusiness on purpose: a landing page can be
              built before any business exists, and assigning one is how a
              captured lead later becomes a business. Gating it would make the
              feature unreachable for exactly the new user it's aimed at. */}
          <Route path="/landing-pages" element={<LandingPagesList />} />
          {/* One dashboard, three URLs. The tab is in the path so it can be
              bookmarked and so the back button steps between tabs — and so the
              "new lead" email we send can keep pointing at /leads, which is
              where every notification already in somebody's inbox links to. */}
          <Route path="/landing-pages/:siteId/dashboard" element={<LandingSiteDashboard />} />
          <Route path="/landing-pages/:siteId/pages" element={<LandingSiteDashboard />} />
          <Route path="/landing-pages/:siteId/navigation" element={<LandingSiteDashboard />} />
          <Route path="/landing-pages/:siteId/leads" element={<LandingSiteDashboard />} />
          <Route path="/landing-pages/:siteId/email" element={<LandingSiteDashboard />} />
          <Route path="/landing-pages/:siteId/settings" element={<LandingSiteDashboard />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/profile" element={<Profile />} />
        </Route>

        {/* Standalone admin — no app sidebar/layout */}
        <Route
          path="/adminwemanage"
          element={
            <ProtectedRoute skipPlanGate>
              <AdminWeManage />
            </ProtectedRoute>
          }
        />

        {/* The page builder takes the whole viewport. Outside AppLayout on
            purpose: an editor competing with two nav rails for horizontal
            space has no room left to show the page being edited. */}
        <Route
          path="/landing-pages/:siteId/pages/:pageId/edit"
          element={
            <ProtectedRoute>
              <LandingBuilder />
            </ProtectedRoute>
          }
        />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
